import { executeDeepReasoningJob, waitForDeepReasoningBatchRunCompletion, type BatchRunData } from './deepReasoningJob';

// ========================================
// バッチ処理ベストプラクティス設定
// ========================================
const MAX_PAGES_PER_BATCH = 100;  // 1バッチ最大100ページ
const CONCURRENT_BATCHES = 2;      // 同時実行2バッチまで

/**
 * deepReasoning の検索提案生成を Cloud Run Jobs で実行する（フロントからの長時間実行用）
 * 
 * ⚠️ 非推奨: 大量ページの場合は requestDeepReasoningSuggestionsWithBatching を使用してください
 */
export async function requestDeepReasoningSuggestions(
  workspaceId: string,
  pageIds: string[],
  options?: {
    languageCode?: string;
    onProgress?: (data: BatchRunData) => void;
  }
): Promise<BatchRunData> {
  const { runId } = await executeDeepReasoningJob({
    workspaceId,
    pageIds,
    languageCode: options?.languageCode,
    taskInnerConcurrency: 2  // 固定値（ベストプラクティス）
  });

  return await waitForDeepReasoningBatchRunCompletion({
    workspaceId,
    runId,
    onProgress: options?.onProgress
  });
}

/**
 * 100ページずつバッチ分割して実行（推奨）
 * 
 * 【ベストプラクティス】
 * - 100ページずつバッチ分割（タイムアウト回避）
 * - 2バッチまで並列実行（OpenAIレート制限考慮）
 * - taskInnerConcurrency=2 固定
 * 
 * @example
 * // 244ページの場合: 3バッチに自動分割
 * // バッチ1(100p) + バッチ2(100p) を並列実行 → バッチ3(44p)
 */
export async function requestDeepReasoningSuggestionsWithBatching(
  workspaceId: string,
  pageIds: string[],
  options?: {
    languageCode?: string;
    onProgress?: (data: {
      currentBatch: number;
      totalBatches: number;
      batchProgress: BatchRunData;
      overallProgress: {
        totalPages: number;
        processedPages: number;
        successPages: number;
        failedPages: number;
        skippedPages: number;
        totalSuggestions: number;
      };
    }) => void;
  }
): Promise<{
  totalPages: number;
  processedPages: number;
  successPages: number;
  failedPages: number;
  skippedPages: number;
  totalSuggestions: number;
  batches: BatchRunData[];
}> {
  // バッチ分割
  const batches: string[][] = [];
  for (let i = 0; i < pageIds.length; i += MAX_PAGES_PER_BATCH) {
    batches.push(pageIds.slice(i, i + MAX_PAGES_PER_BATCH));
  }

  console.log(`📦 [Batching] ${pageIds.length}ページを${batches.length}バッチに分割`);

  const results: BatchRunData[] = [];
  let overallStats = {
    totalPages: pageIds.length,
    processedPages: 0,
    successPages: 0,
    failedPages: 0,
    skippedPages: 0,
    totalSuggestions: 0
  };

  // 2バッチずつ並列実行
  for (let i = 0; i < batches.length; i += CONCURRENT_BATCHES) {
    const currentBatches = batches.slice(i, i + CONCURRENT_BATCHES);
    const batchIndices = currentBatches.map((_, idx) => i + idx);

    console.log(`🚀 [Batching] バッチ ${batchIndices.map(idx => idx + 1).join(', ')}/${batches.length} を並列実行開始`);

    // 並列実行
    const promises = currentBatches.map(async (batchPages, idx) => {
      const batchIndex = batchIndices[idx];
      console.log(`  ├─ バッチ ${batchIndex + 1}: ${batchPages.length}ページ`);

      const result = await requestDeepReasoningSuggestions(workspaceId, batchPages, {
        languageCode: options?.languageCode,
        onProgress: (data) => {
          // バッチごとの進捗を全体進捗に統合
          const batchProcessed = Number(data.processedPages || 0);
          const previousBatchesTotal = batchIndex * MAX_PAGES_PER_BATCH;
          const currentOverallProcessed = previousBatchesTotal + batchProcessed;

          // 全体進捗を計算（完了済みバッチ + 現在のバッチ進捗）
          const completedBatchesPages = results.reduce((sum, r) => sum + Number(r.processedPages || 0), 0);
          overallStats.processedPages = completedBatchesPages + batchProcessed;
          overallStats.successPages = results.reduce((sum, r) => sum + Number(r.successPages || 0), 0) + Number(data.successPages || 0);
          overallStats.failedPages = results.reduce((sum, r) => sum + Number(r.failedPages || 0), 0) + Number(data.failedPages || 0);
          overallStats.skippedPages = results.reduce((sum, r) => sum + Number(r.skippedPages || 0), 0) + Number(data.skippedPages || 0);
          overallStats.totalSuggestions = results.reduce((sum, r) => sum + Number(r.totalSuggestions || 0), 0) + Number(data.totalSuggestions || 0);

          options?.onProgress?.({
            currentBatch: batchIndex + 1,
            totalBatches: batches.length,
            batchProgress: data,
            overallProgress: { ...overallStats }
          });
        }
      });

      console.log(`  ✅ バッチ ${batchIndex + 1} 完了: ${result.successPages}/${result.totalPages}ページ成功`);
      return result;
    });

    const batchResults = await Promise.all(promises);
    results.push(...batchResults);

    // 統計を更新
    overallStats.processedPages = results.reduce((sum, r) => sum + Number(r.processedPages || 0), 0);
    overallStats.successPages = results.reduce((sum, r) => sum + Number(r.successPages || 0), 0);
    overallStats.failedPages = results.reduce((sum, r) => sum + Number(r.failedPages || 0), 0);
    overallStats.skippedPages = results.reduce((sum, r) => sum + Number(r.skippedPages || 0), 0);
    overallStats.totalSuggestions = results.reduce((sum, r) => sum + Number(r.totalSuggestions || 0), 0);

    console.log(`✅ [Batching] ${batchIndices.map(idx => idx + 1).join(', ')}/${batches.length} 完了`);
  }

  console.log(`🎉 [Batching] 全バッチ完了: ${overallStats.successPages}/${overallStats.totalPages}ページ成功`);

  return {
    ...overallStats,
    batches: results
  };
}